import { toast } from "vue3-toastify";
import 'vue3-toastify/dist/index.css';
import apiClient from "@/axios";
import { saveClaimToken, getClaimToken, clearClaimToken } from "@/utils/claimTokens";
import { apiErrorMessage } from "@/utils/apiError";

const API_PATHS = {
    PASTES: 'pastes',
};

const state = {
    pastes: null,
    paste: null,
    error: null,
    isLoading: false,
};

const getters = {
    statePastes: state => state.pastes,
    statePaste: state => state.paste,
    statePasteError: state => state.error,
    stateIsLoading: state => state.isLoading,
};

const actions = {
    async getPastes({ commit }) {
        try {
            commit('setLoading', true);
            const response = await apiClient.get(API_PATHS.PASTES + '/');
            commit('setPastes', response.data);
        } catch (error) {
            console.error('Error fetching pastes:', error);
            const errorMessage = apiErrorMessage(error, "Failed to load pastes.");
            commit('setError', errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async getPaste({ commit }, pasteId) {
        commit('setLoading', true);
        try {
            const response = await apiClient.get(`${API_PATHS.PASTES}/${pasteId}`);
            commit('setPaste', response.data);
        } catch (error) {
            console.log(error);
            const errorMessage = apiErrorMessage(error, "Failed to load paste.");
            commit('setError', errorMessage);
        } finally {
            commit('setLoading', false);
        }
    },

    // Content fetch (POST — the gated content endpoint; owners bypass
    // password/burn, visitors may need to send one). Accepts a bare pasteId
    // or { pasteId, password }. Returned, not committed: it's per-view data.
    async getPasteContent(_context, arg) {
        const { pasteId, password } = typeof arg === 'string' ? { pasteId: arg } : arg;
        const body = password ? { password } : {};
        const response = await apiClient.post(`${API_PATHS.PASTES}/${pasteId}/content`, body);
        return response.data;
    },

    async createPaste({ commit }, { content, language, title, expires, viewPassword, burnAfterRead }) {
        try {
            commit('setLoading', true);
            const payload = { content, language };
            if (title) payload.title = title;
            if (expires) payload.expires = expires;
            if (viewPassword) payload.view_password = viewPassword;
            if (burnAfterRead) payload.burn_after_read = true;

            const response = await apiClient.post(API_PATHS.PASTES + '/', payload);
            // Anonymous pastes come back with a one-time claim token.
            if (response.data && response.data.claim_token) {
                saveClaimToken('paste', response.data.paste_id, response.data.claim_token);
            }
            commit('addPaste', response.data);
            toast.success('Paste created successfully');
            return response.data;
        } catch (error) {
            console.error('Error creating paste:', error);
            const errorMessage = apiErrorMessage(error, "Failed to create paste.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    // Content is immutable; only title/expiry/password/burn may change.
    // viewPassword semantics: undefined = unchanged, null = clear, string = set.
    async updatePaste({ commit }, { pasteId, title, expires, viewPassword, burnAfterRead }) {
        try {
            commit('setLoading', true);
            const payload = {};
            if (title !== undefined) payload.title = title;
            if (expires !== undefined) payload.expires = expires;
            if (viewPassword !== undefined) payload.view_password = viewPassword;
            if (burnAfterRead !== undefined) payload.burn_after_read = burnAfterRead;
            const response = await apiClient.put(`${API_PATHS.PASTES}/${pasteId}`, payload);
            commit('setPaste', response.data);
            commit('updatePaste', response.data);
            toast.success('Paste updated successfully');
            return response.data;
        } catch (error) {
            console.error('Error updating paste:', error);
            const errorMessage = apiErrorMessage(error, "Failed to update paste.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async deletePaste({ commit }, pasteId) {
        try {
            commit('setLoading', true);
            await apiClient.delete(`${API_PATHS.PASTES}/${pasteId}`);
            commit('removePaste', pasteId);
            toast.success('Paste deleted successfully');
        } catch (error) {
            console.error('Error deleting paste:', error);
            const errorMessage = apiErrorMessage(error, "Failed to delete paste.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async claimPaste({ commit }, pasteId) {
        try {
            commit('setLoading', true);
            const response = await apiClient.post(
                `${API_PATHS.PASTES}/${pasteId}/claim`,
                { claim_token: getClaimToken('paste', pasteId) }
            );
            clearClaimToken('paste', pasteId);
            commit('updatePaste', response.data);
            toast.success('Paste claimed successfully');
            return response.data;
        } catch (error) {
            console.error('Error claiming paste:', error);
            const errorMessage = apiErrorMessage(error, "Failed to claim paste.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
};

const mutations = {
    setPastes(state, pastes) {
        state.pastes = pastes;
    },
    setPaste(state, paste) {
        state.paste = paste;
    },
    addPaste(state, paste) {
        if (!state.pastes) state.pastes = [];
        state.pastes.unshift(paste);
    },
    updatePaste(state, updatedPaste) {
        if (state.pastes) {
            const index = state.pastes.findIndex(p => p.paste_id === updatedPaste.paste_id);
            if (index !== -1) {
                state.pastes.splice(index, 1, updatedPaste);
            }
        }
        if (state.paste && state.paste.paste_id === updatedPaste.paste_id) {
            state.paste = updatedPaste;
        }
    },
    removePaste(state, pasteId) {
        if (state.pastes) {
            state.pastes = state.pastes.filter(p => p.paste_id !== pasteId);
        }
        if (state.paste && state.paste.paste_id === pasteId) {
            state.paste = null;
        }
    },
    setError(state, error) {
        state.error = error;
    },
    setLoading(state, isLoading) {
        state.isLoading = isLoading;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
